// This file is auto-generated by @hey-api/openapi-ts

import { z } from 'zod';

/**
 * ValidationError
 */
export const zValidationError = z.object({
    loc: z.array(z.union([z.string(), z.int()])),
    msg: z.string(),
    type: z.string()
});

export const zJsonValue = z.unknown();

/**
 * TrendsOverTime
 */
export const zTrendsOverTime = z.object({
    labels: z.array(z.string()),
    brands: z.array(zJsonValue)
});

/**
 * TrendAnalysis
 */
export const zTrendAnalysis = z.object({
    viral_potential: z.number()
});

/**
 * TraditionalResponse
 */
export const zTraditionalResponse = z.object({
    age: z.array(z.string()),
    gender: z.array(z.string()),
    nationality: z.array(z.string()),
    city: z.array(z.string()),
    sec: z.array(z.string()),
    time: z.array(z.string())
});

/**
 * Metric
 */
export const zMetric = z.object({
    label: z.string(),
    value: z.number()
});

/**
 * MetricSubChart
 */
export const zMetricSubChart = z.object({
    age: z.array(zMetric),
    gender: z.array(zMetric),
    nationality: z.array(zMetric),
    sec: z.array(zMetric),
    city: z.array(zMetric),
    overall: z.array(zMetric)
});

/**
 * CrossTabsChart
 */
export const zCrossTabsChart = z.object({
    Consideration: zMetricSubChart,
    Awareness: zMetricSubChart,
    Usage: zMetricSubChart,
    'Recent Usage': zMetricSubChart,
    'Primary Brand': zMetricSubChart,
    Loyalty: zMetricSubChart
});

/**
 * Dataset
 */
export const zDataset = z.object({
    label: z.string(),
    data: z.array(z.number()),
    borderColor: z.optional(z.union([
        z.string(),
        z.null()
    ])),
    backgroundColor: z.union([
        z.string(),
        z.array(z.string())
    ]),
    tension: z.optional(z.union([
        z.number(),
        z.null()
    ]))
});

/**
 * AwarenessOverTime
 */
export const zAwarenessOverTime = z.object({
    labels: z.array(z.string()),
    datasets: z.array(zDataset)
});

/**
 * TraditionalCharts
 */
export const zTraditionalCharts = z.object({
    awareness_over_time: zAwarenessOverTime,
    demographics_metrics: z.record(z.string(), z.array(zMetric)),
    media_reach: zAwarenessOverTime,
    cross_tabs: zCrossTabsChart
});

/**
 * SocialMediaStats
 */
export const zSocialMediaStats = z.object({
    followers: z.number(),
    engagements: z.number(),
    engagement_rate: z.number(),
    growth_rate: z.optional(z.number()).default(0),
    sentiment_score: z.number(),
    share_of_voice: z.optional(z.number()).default(0),
    engagement_growth: z.optional(z.number()).default(0)
});

/**
 * InterestStats
 */
export const zInterestStats = z.object({
    name: z.string(),
    interest: z.number()
});

/**
 * SearchTrends
 */
export const zSearchTrends = z.object({
    search_interest_score: z.number(),
    last_month_score: z.number(),
    competitor_gap: z.number(),
    trend_direction: z.enum(['up', 'down']),
    trend_change: z.number(),
    peak_period: z.string(),
    seasonal_index: z.number(),
    current_season: z.string(),
    competitor_comparison: z.array(zInterestStats),
    trends_over_time: zTrendsOverTime
});

/**
 * PaidMedia
 */
export const zPaidMedia = z.object({
    channels: z.record(z.string(), z.record(z.string(), z.number())),
    overall_roas: z.number(),
    cost_per_acquisition: z.number(),
    budget_utilization: z.number(),
    attribution_model: z.string()
});

/**
 * PageInsightsResponse
 */
export const zPageInsightsResponse = z.object({
    page: z.string(),
    insights: z.string()
});

/**
 * PageInsightsRequest
 */
export const zPageInsightsRequest = z.object({
    brand_ids: z.array(z.int()),
    filters: z.optional(z.union([
        zTraditionalResponse,
        z.null()
    ]))
});

/**
 * Metrics
 */
export const zMetrics = z.object({
    total_awareness: z.number(),
    total_consideration: z.number(),
    total_p12m_usage: z.number(),
    total_recent_usage: z.number(),
    total_top_of_mind: z.number(),
    total_spontaneous: z.number(),
    total_aided: z.number(),
    total_moub: z.number(),
    awareness: z.number(),
    consideration: z.number(),
    usage: z.number(),
    recent_usage: z.number(),
    primary_brand: z.number(),
    loyalty: z.number(),
    advocacy: z.number(),
    satisfaction: z.number(),
    overall: z.number(),
    total_count: z.number(),
    nps: z.number(),
    trust: z.number(),
    pride: z.number(),
    innovation: z.number(),
    value: z.number(),
    quality: z.number(),
    taste: z.number(),
    texture: z.number(),
    natural: z.number()
});

/**
 * MessageOut
 */
export const zMessageOut = z.object({
    id: z.string(),
    role: z.string(),
    content: z.union([
        z.string(),
        z.null()
    ]),
    created_at: z.union([
        z.string(),
        z.null()
    ])
});

/**
 * Interval
 *
 * Period definition which can be closed, semi-open, or open
 */
export const zInterval = z.object({
    start: z.optional(z.union([
        z.iso.datetime(),
        z.null()
    ])),
    end: z.optional(z.union([
        z.iso.datetime(),
        z.null()
    ]))
});

/**
 * Item[int]
 */
export const zItemInt = z.object({
    id: z.int(),
    name: z.string()
});

/**
 * MeResponse
 */
export const zMeResponse = z.object({
    email: z.email(),
    name: z.union([
        z.string(),
        z.null()
    ]),
    orgs: z.array(zItemInt),
    current_org_brands: z.array(zItemInt),
    current_org_period: zInterval
});

/**
 * CompetitionAnalysis
 */
export const zCompetitionAnalysis = z.object({
    share_of_voice: z.number()
});

/**
 * ContentTypeStats
 */
export const zContentTypeStats = z.object({
    content_type: z.string(),
    avg_views: z.number(),
    avg_likes: z.number(),
    avg_comments: z.number(),
    avg_shares: z.number(),
    avg_engagement_rate: z.number()
});

/**
 * ContentPerformanceStats
 */
export const zContentPerformanceStats = z.object({
    content_types: z.array(zContentTypeStats),
    content_roi: z.number(),
    content_velocity: z.number(),
    content_effectiveness: z.number(),
    evergreen_ratio: z.number()
});

/**
 * BrandSocialMedia
 */
export const zBrandSocialMedia = z.object({
    total_followers: z.number(),
    avg_engagement_rate: z.number(),
    community_health: z.optional(z.union([
        z.number(),
        z.null()
    ])),
    platforms: z.record(z.string(), zSocialMediaStats)
});

/**
 * FullDigitalData
 */
export const zFullDigitalData = z.object({
    mentions_count: z.number(),
    sentiment_score: z.number(),
    share_of_voice: z.number(),
    engagement_rate: z.number(),
    total_engagements: z.number(),
    engagement_growth: z.optional(z.number()).default(0),
    positive_mentions: z.number(),
    negative_mentions: z.number(),
    neutral_mentions: z.number(),
    brand_health: z.number(),
    community_health: z.number(),
    trending_topics: z.optional(z.union([
        z.array(z.string()),
        z.null()
    ])),
    viral_coefficient: z.optional(z.union([
        z.number(),
        z.null()
    ])),
    insights: z.optional(z.union([
        z.array(z.string()),
        z.null()
    ])),
    social_media: zBrandSocialMedia,
    content_performance: z.optional(z.union([
        zContentPerformanceStats,
        z.null()
    ])),
    search_trends: z.optional(z.union([
        zSearchTrends,
        z.null()
    ])),
    paid_media: z.optional(z.union([
        zPaidMedia,
        z.null()
    ])),
    competitor_analysis: zCompetitionAnalysis,
    trend_analysis: z.optional(z.union([
        zTrendAnalysis,
        z.null()
    ]))
});

/**
 * BrandMetricsResponse
 */
export const zBrandMetricsResponse = z.object({
    brand_id: z.string(),
    name: z.string(),
    category: z.string(),
    traditional_charts: zTraditionalCharts,
    metrics: zMetrics,
    respondent_count: z.int()
});

/**
 * BrandEquity
 */
export const zBrandEquity = z.object({
    trad_equity: z.number(),
    digital_equity: z.number(),
    total_equity: z.number(),
    brand_index: z.number(),
    community_index: z.number()
});

/**
 * HealthIndustryAverage
 */
export const zHealthIndustryAverage = z.object({
    awareness: z.int(),
    consideration: z.int(),
    usage: z.int(),
    moub: z.int(),
    loyalty: z.int()
});

/**
 * HealthBreakdown
 */
export const zHealthBreakdown = z.object({
    awareness_score: z.int(),
    trust_score: z.int(),
    quality_score: z.int(),
    nps_score: z.int(),
    loyalty_score: z.int()
});

/**
 * HealthResponse
 */
export const zHealthResponse = z.object({
    health_index: z.number(),
    percentile: z.number(),
    category: z.string(),
    rank: z.number(),
    total_brands: z.number(),
    trend: z.enum(['up', 'down']),
    change: z.number(),
    breakdown: zHealthBreakdown,
    industry_averages: zHealthIndustryAverage,
    equity: zBrandEquity,
    traditional: zBrandMetricsResponse,
    digital: z.union([
        zFullDigitalData,
        z.null()
    ])
});

/**
 * HTTPValidationError
 */
export const zHttpValidationError = z.object({
    detail: z.optional(z.array(zValidationError))
});

/**
 * FiltersResponse
 */
export const zFiltersResponse = z.object({
    field: z.string(),
    values: z.array(z.string()),
    type: z.enum([
        'common',
        'traditional',
        'digital'
    ])
});

/**
 * DigitalResponse
 */
export const zDigitalResponse = z.object({
    age: z.array(z.string()),
    data_source: z.array(z.string()),
    channel: z.array(z.string()),
    gender: z.array(z.string()),
    country: z.array(z.string()),
    time: z.array(z.string())
});

/**
 * BrandPost
 */
export const zBrandPost = z.object({
    id: z.string(),
    platform: z.string(),
    content: z.string(),
    author: z.string(),
    date: z.string(),
    likes: z.int(),
    comments: z.int(),
    language: z.string(),
    sentiment: z.string(),
    postType: z.string()
});

/**
 * CursorResponse
 */
export const zCursorResponse = z.object({
    data: z.array(zBrandPost),
    nextCursor: z.union([
        z.string(),
        z.null()
    ])
});

/**
 * ConversationSummary
 */
export const zConversationSummary = z.object({
    id: z.string(),
    title: z.union([
        z.string(),
        z.null()
    ]),
    created_at: z.union([
        z.string(),
        z.null()
    ]),
    updated_at: z.union([
        z.string(),
        z.null()
    ])
});

/**
 * ChatResponse
 */
export const zChatResponse = z.object({
    content: z.string(),
    conversation_id: z.string()
});

/**
 * ChatRequest
 */
export const zChatRequest = z.object({
    query: z.string(),
    conversation_id: z.optional(z.union([
        z.string(),
        z.null()
    ]))
});

/**
 * Body_get_traditional_brand_metrics_api_v3_metrics_brands_trad_post
 */
export const zBodyGetTraditionalBrandMetricsApiV3MetricsBrandsTradPost = z.object({
    brand_ids: z.array(z.int()),
    filters: z.optional(z.union([
        zTraditionalResponse,
        z.null()
    ]))
});

/**
 * Body_get_digital_brand_metrics_api_v3_metrics_brands_digital_post
 */
export const zBodyGetDigitalBrandMetricsApiV3MetricsBrandsDigitalPost = z.object({
    brand_ids: z.array(z.int()),
    filters: z.optional(z.union([
        zDigitalResponse,
        z.null()
    ]))
});

/**
 * Body_get_brand_posts_api_v3_metrics_brands__brand_id__posts_post
 */
export const zBodyGetBrandPostsApiV3MetricsBrandsBrandIdPostsPost = z.object({
    filters: z.optional(z.union([
        zDigitalResponse,
        z.null()
    ])),
    platform: z.optional(z.array(z.enum([
        'Facebook',
        'Instagram',
        'Linkedin',
        'Tiktok',
        'Twitter',
        'Youtube'
    ]))).default([]),
    sentiment: z.optional(z.array(z.enum([
        'Negative',
        'Positive',
        'Neutral'
    ]))).default([])
});

/**
 * Body_get_brand_health_api_v3_metrics_brands_health_post
 */
export const zBodyGetBrandHealthApiV3MetricsBrandsHealthPost = z.object({
    brand_ids: z.array(z.int()),
    filters: z.optional(z.union([
        zTraditionalResponse,
        z.null()
    ]))
});

export const zGetMeApiV3MeGetData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        org: z.optional(z.union([
            z.string(),
            z.null()
        ]))
    })),
    headers: z.optional(z.object({
        'x-org-id': z.optional(z.union([
            z.string(),
            z.null()
        ]))
    }))
});

/**
 * Successful Response
 */
export const zGetMeApiV3MeGetResponse = zMeResponse;

export const zPostChatMessageApiV3ChatPostData = z.object({
    body: zChatRequest,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        org: z.optional(z.union([
            z.string(),
            z.null()
        ]))
    })),
    headers: z.optional(z.object({
        'x-org-id': z.optional(z.union([
            z.string(),
            z.null()
        ]))
    }))
});

/**
 * Successful Response
 */
export const zPostChatMessageApiV3ChatPostResponse = zChatResponse;

export const zGetConversationsApiV3ConversationsGetData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        limit: z.optional(z.int().gte(1).lte(200)).default(50),
        offset: z.optional(z.int().gte(0)).default(0),
        org: z.optional(z.union([
            z.string(),
            z.null()
        ]))
    })),
    headers: z.optional(z.object({
        'x-org-id': z.optional(z.union([
            z.string(),
            z.null()
        ]))
    }))
});

/**
 * Response Get Conversations Api V3 Conversations Get
 *
 * Successful Response
 */
export const zGetConversationsApiV3ConversationsGetResponse = z.array(zConversationSummary);

export const zGetMessagesApiV3ConversationsConversationIdMessagesGetData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        conversation_id: z.string()
    }),
    query: z.optional(z.object({
        org: z.optional(z.union([
            z.string(),
            z.null()
        ]))
    })),
    headers: z.optional(z.object({
        'x-org-id': z.optional(z.union([
            z.string(),
            z.null()
        ]))
    }))
});

/**
 * Response Get Messages Api V3 Conversations  Conversation Id  Messages Get
 *
 * Successful Response
 */
export const zGetMessagesApiV3ConversationsConversationIdMessagesGetResponse = z.array(zMessageOut);

export const zGetFiltersApiV3MetricsFiltersGetData = z.object({
    body: z.optional(z.never()),
    path: z.optional(z.never()),
    query: z.optional(z.object({
        org: z.optional(z.union([
            z.string(),
            z.null()
        ]))
    })),
    headers: z.optional(z.object({
        'x-org-id': z.optional(z.union([
            z.string(),
            z.null()
        ]))
    }))
});

/**
 * Response Get Filters Api V3 Metrics Filters Get
 *
 * Successful Response
 */
export const zGetFiltersApiV3MetricsFiltersGetResponse = z.array(zFiltersResponse);

export const zGetTraditionalBrandMetricsApiV3MetricsBrandsTradPostData = z.object({
    body: zBodyGetTraditionalBrandMetricsApiV3MetricsBrandsTradPost,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        segment_id: z.optional(z.union([
            z.string(),
            z.null()
        ])),
        month: z.optional(z.union([
            z.string(),
            z.null()
        ])),
        org: z.optional(z.union([
            z.string(),
            z.null()
        ]))
    })),
    headers: z.optional(z.object({
        'x-org-id': z.optional(z.union([
            z.string(),
            z.null()
        ]))
    }))
});

/**
 * Response Get Traditional Brand Metrics Api V3 Metrics Brands Trad Post
 *
 * Successful Response
 */
export const zGetTraditionalBrandMetricsApiV3MetricsBrandsTradPostResponse = z.record(z.string(), z.union([
    zBrandMetricsResponse,
    z.null()
]));

export const zGetDigitalBrandMetricsApiV3MetricsBrandsDigitalPostData = z.object({
    body: zBodyGetDigitalBrandMetricsApiV3MetricsBrandsDigitalPost,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        source: z.optional(z.union([
            z.enum(['brand', 'community']),
            z.null()
        ])),
        org: z.optional(z.union([
            z.string(),
            z.null()
        ]))
    })),
    headers: z.optional(z.object({
        'x-org-id': z.optional(z.union([
            z.string(),
            z.null()
        ]))
    }))
});

/**
 * Response Get Digital Brand Metrics Api V3 Metrics Brands Digital Post
 *
 * Successful Response
 */
export const zGetDigitalBrandMetricsApiV3MetricsBrandsDigitalPostResponse = z.record(z.string(), z.union([
    zFullDigitalData,
    z.null()
]));

export const zGetBrandHealthApiV3MetricsBrandsHealthPostData = z.object({
    body: zBodyGetBrandHealthApiV3MetricsBrandsHealthPost,
    path: z.optional(z.never()),
    query: z.optional(z.object({
        org: z.optional(z.union([
            z.string(),
            z.null()
        ]))
    })),
    headers: z.optional(z.object({
        'x-org-id': z.optional(z.union([
            z.string(),
            z.null()
        ]))
    }))
});

/**
 * Response Get Brand Health Api V3 Metrics Brands Health Post
 *
 * Successful Response
 */
export const zGetBrandHealthApiV3MetricsBrandsHealthPostResponse = z.record(z.string(), z.union([
    zHealthResponse,
    z.null()
]));

export const zGetBrandInsightsApiV3MetricsBrandsBrandIdInsightsPostData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        brand_id: z.int()
    }),
    query: z.optional(z.object({
        org: z.optional(z.union([
            z.string(),
            z.null()
        ]))
    })),
    headers: z.optional(z.object({
        'x-org-id': z.optional(z.union([
            z.string(),
            z.null()
        ]))
    }))
});

/**
 * Response Get Brand Insights Api V3 Metrics Brands  Brand Id  Insights Post
 *
 * Successful Response
 */
export const zGetBrandInsightsApiV3MetricsBrandsBrandIdInsightsPostResponse = z.array(z.string());

export const zGetBrandTabAnalysisApiV3MetricsBrandsBrandIdAnalysisTabPostData = z.object({
    body: z.optional(z.never()),
    path: z.object({
        brand_id: z.int(),
        tab: z.string()
    }),
    query: z.optional(z.object({
        org: z.optional(z.union([
            z.string(),
            z.null()
        ]))
    })),
    headers: z.optional(z.object({
        'x-org-id': z.optional(z.union([
            z.string(),
            z.null()
        ]))
    }))
});

/**
 * Response Get Brand Tab Analysis Api V3 Metrics Brands  Brand Id  Analysis  Tab  Post
 *
 * Successful Response
 */
export const zGetBrandTabAnalysisApiV3MetricsBrandsBrandIdAnalysisTabPostResponse = z.string();

export const zGetBrandPostsApiV3MetricsBrandsBrandIdPostsPostData = z.object({
    body: z.optional(zBodyGetBrandPostsApiV3MetricsBrandsBrandIdPostsPost),
    path: z.object({
        brand_id: z.int()
    }),
    query: z.optional(z.object({
        data_source: z.optional(z.enum([
            'Brand',
            'Community',
            'all'
        ])),
        order_by: z.optional(z.enum(['recent', 'engagement'])),
        limit: z.optional(z.int()).default(12),
        cursor: z.optional(z.union([
            z.string(),
            z.null()
        ])),
        org: z.optional(z.union([
            z.string(),
            z.null()
        ]))
    })),
    headers: z.optional(z.object({
        'x-org-id': z.optional(z.union([
            z.string(),
            z.null()
        ]))
    }))
});

/**
 * Successful Response
 */
export const zGetBrandPostsApiV3MetricsBrandsBrandIdPostsPostResponse = zCursorResponse;

export const zGetPageInsightsApiV3InsightsPagePostData = z.object({
    body: zPageInsightsRequest,
    path: z.object({
        page: z.enum([
            'executive',
            'deepdive-awareness',
            'deepdive-funnel',
            'deepdive-attributes',
            'deepdive-media',
            'deepdive-crosstab',
            'digital-social',
            'digital-content',
            'digital-search',
            'digital-paid',
            'comparison',
            'posts'
        ])
    }),
    query: z.optional(z.object({
        org: z.optional(z.union([
            z.string(),
            z.null()
        ]))
    })),
    headers: z.optional(z.object({
        'x-org-id': z.optional(z.union([
            z.string(),
            z.null()
        ]))
    }))
});

/**
 * Successful Response
 */
export const zGetPageInsightsApiV3InsightsPagePostResponse = zPageInsightsResponse;
